/**
 * ScanHistory (Jul-2026, User-Wunsch) — fortlaufende Liste aller gescannten
 * Lieferscheine/Rechnungen mit Universal-Suche (alle Felder) + 10/20/50-Auswahl.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, Loader2, ShoppingCart, Landmark, ArrowRight, Eye, PackageCheck, XCircle, Activity, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { normalizeImageUrl } from "../../lib/imageUrl";
import { Input } from "../ui/input";

const DOC_LABEL = { delivery_note: "Lieferschein", invoice: "Rechnung", combined: "LS = Rechnung", own_invoice: "Eigene Rechnung", maintenance_coupon: "Wartungscoupon" };
// Aug-2026 (User-Wunsch): Herkunft des Scans sichtbar machen.
const ORIGIN_LABEL = { inbox: "Posteingang", scan_page: "Lieferschein-Scan" };
const OriginBadge = ({ origin }) => origin ? (
    <span className={`inline-flex text-[10px] font-bold rounded px-1.5 py-0.5 ${origin === "inbox" ? "bg-indigo-100 text-indigo-800" : "bg-sky-100 text-sky-800"}`}
        title={origin === "inbox" ? "Kam über den Dokumenten-Posteingang (überwachter Ordner)" : "Kam über die Seite ‚Lieferschein scannen'"}>
        {ORIGIN_LABEL[origin] || origin}
    </span>
) : <span className="text-gray-300">—</span>;

export default function ScanHistory({ reload = 0 }) {
    const [q, setQ] = useState("");
    const [limit, setLimit] = useState(10);
    const [items, setItems] = useState([]);
    const [unbooked, setUnbooked] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [bookingId, setBookingId] = useState(null);

    const [diag, setDiag] = useState(null);
    const [diagBusy, setDiagBusy] = useState(false);

    // Aug-2026 (User-Frage „wie können wir sicherstellen?"): System-Check —
    // prüft, ob die Performance-Indexe aktiv sind + zeigt letzte Scan-Laufzeiten.
    const runDiagnose = async () => {
        setDiagBusy(true);
        try {
            const r = await api.get("/wms/scan-diagnose");
            setDiag(r.data);
            if (r.data?.ok) toast.success("System-Check OK — alle Beschleunigungs-Indexe aktiv");
            else toast.error("System-Check: Indexe fehlen! Bitte neu deployen bzw. Support kontaktieren.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "System-Check fehlgeschlagen");
        }
        setDiagBusy(false);
    };

    // Aug-2026 (User-Wunsch): ungebuchte Scans mit einem Klick nachverbuchen
    const bookJob = async (j) => {
        setBookingId(j.id);
        try {
            const r = await api.post(`/wms/delivery-note-scans/book-job/${j.id}`);
            const res = r.data?.results || [];
            const ok = res.filter((x) => x.ok);
            const dup = res.filter((x) => !x.ok && x.duplicate);
            const fail = res.filter((x) => !x.ok && !x.duplicate);
            if (ok.length) {
                const b = ok.reduce((s, x) => s + (x.booked || 0), 0);
                const c = ok.reduce((s, x) => s + (x.created || 0), 0);
                toast.success(`Verbucht: ${b} Position(en) zugebucht${c ? `, ${c} Artikel neu angelegt` : ""}`);
            }
            if (dup.length) toast.info("Beleg war bereits verbucht (Doppel-Scan erkannt)");
            fail.forEach((x) => toast.error(`${x.file_name}: ${x.error}`));
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verbuchen fehlgeschlagen");
        }
        setBookingId(null);
    };

    // Aug-2026 (User-Wunsch): hängengebliebene/nicht buchbare Scans entfernen
    const dismissJob = async (j) => {
        if (!window.confirm(`„${j.file_name || "Scan"}" aus der Liste entfernen? (Der Beleg wird NICHT gebucht.)`)) return;
        try {
            await api.post(`/wms/delivery-note-scans/dismiss-job/${j.id}`);
            toast.success("Eintrag entfernt");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/wms/delivery-note-scans", { params: { limit, q } });
            setItems(r.data.items || []);
            setUnbooked(r.data.unbooked || []);
            setTotal(r.data.total || 0);
        } catch { /* noop */ }
        setLoading(false);
    }, [limit, q]);

    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reload]);

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5 mt-6" data-testid="scan-history-block">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <h2 className="font-bold text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Gescannte Lieferscheine / Rechnungen
                    <span className="text-xs font-normal text-gray-400">({total})</span>
                </h2>
                <div className="flex items-center gap-2">
                    <button onClick={runDiagnose} disabled={diagBusy}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-600 hover:text-gray-900 border border-gray-300 rounded-sm px-2 h-9 disabled:opacity-50"
                        title="Prüft, ob alle Beschleunigungs-Indexe aktiv sind und zeigt die letzten Scan-Laufzeiten"
                        data-testid="scan-diagnose-btn">
                        {diagBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />} System-Check
                    </button>
                    <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Alle Felder durchsuchen…"
                            className="pl-8 rounded-sm h-9 w-56" data-testid="scan-history-search" />
                    </div>
                    <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                        className="border border-gray-300 rounded-sm px-2 h-9 text-sm bg-white" data-testid="scan-history-limit">
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            {/* Aug-2026: Ergebnis des System-Checks */}
            {diag && (
                <div className={`mb-4 border rounded-sm p-3 text-xs ${diag.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="scan-diagnose-result">
                    <div className="flex items-center gap-2 font-bold mb-1">
                        {diag.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-600" />}
                        {diag.ok ? "Alle Beschleunigungs-Indexe aktiv" : "Es fehlen Indexe — Scans können hängen!"}
                        <span className="font-normal text-gray-500">· {Number(diag.articles_total || 0).toLocaleString("de-DE")} Artikel in der Datenbank</span>
                        <button onClick={() => setDiag(null)} className="ml-auto text-gray-400 hover:text-gray-700"><XCircle className="w-4 h-4" /></button>
                    </div>
                    {!diag.ok && (
                        <div className="text-red-800 mb-1">
                            Fehlend: {(diag.checks || []).filter((c) => !c.ok).map((c) => `${c.collection} (${c.missing.join(", ")})`).join("; ")}
                        </div>
                    )}
                    {(diag.recent_scans || []).length > 0 && (
                        <div className="text-gray-600">
                            Letzte Scan-Laufzeiten: {(diag.recent_scans || []).map((r) => `${r.seconds}s`).join(" · ")}
                        </div>
                    )}
                </div>
            )}

            {/* Aug-2026 (User-Report Jörg): gescannte, aber noch NICHT gebuchte Belege
                des Standorts sichtbar machen — vorher fehlten sie komplett. */}
            {unbooked.length > 0 && (
                <div className="mb-4 border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="scan-history-unbooked">
                    <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2">
                        Gescannt, aber noch nicht gebucht ({unbooked.length})
                    </div>
                    <ul className="space-y-1">
                        {unbooked.map((j) => (
                            <li key={j.id} className="flex items-center gap-2 text-xs text-gray-800 flex-wrap" data-testid={`scan-history-unbooked-${j.id}`}>
                                <span className="font-mono-num text-[11px] text-gray-500 whitespace-nowrap">{(j.created_at || "").slice(0, 16).replace("T", " ")}</span>
                                <span className="truncate max-w-[320px]" title={j.scan_label ? `Datei: ${j.file_name}` : undefined} data-testid={`scan-unbooked-name-${j.id}`}>
                                    {j.scan_label || j.file_name || "Beleg"}
                                </span>
                                <OriginBadge origin={j.origin} />
                                {j.status === "done" && (
                                    <>
                                        <span className="inline-flex text-[10px] font-bold bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">erkannt · nicht gebucht</span>
                                        {/* Aug-2026 (User-Frage „warum nicht gebucht?"): Grund anzeigen */}
                                        <span className="text-[10px] text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 max-w-[380px] truncate"
                                            title={j.unbooked_reason || "Grund nicht protokolliert (Scan vor dem Update) — bitte ‚Jetzt verbuchen' versuchen"}
                                            data-testid={`scan-unbooked-reason-${j.id}`}>
                                            Grund: {j.unbooked_reason || "nicht protokolliert (älterer Scan)"}
                                        </span>
                                        <button onClick={() => bookJob(j)} disabled={bookingId === j.id}
                                            className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm px-2 py-1 disabled:opacity-50"
                                            data-testid={`scan-history-book-${j.id}`}>
                                            {bookingId === j.id
                                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                                : <PackageCheck className="w-3 h-3" />} Jetzt verbuchen
                                        </button>
                                    </>
                                )}
                                {j.status === "pending" && (
                                    <span className="inline-flex text-[10px] font-bold bg-blue-100 text-blue-800 rounded px-1.5 py-0.5">in Verarbeitung</span>
                                )}
                                {j.status === "error" && (
                                    <span className="inline-flex text-[10px] font-bold bg-red-100 text-red-800 rounded px-1.5 py-0.5" title={j.error}>Fehler</span>
                                )}
                                {j.status !== "pending" && (
                                    <button onClick={() => dismissJob(j)}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-red-700 border border-gray-300 hover:border-red-300 rounded-sm px-2 py-1"
                                        title="Eintrag aus der Liste entfernen (ohne Buchung)"
                                        data-testid={`scan-history-dismiss-${j.id}`}>
                                        <XCircle className="w-3 h-3" /> Entfernen
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left px-3 py-2">Datum</th>
                            <th className="text-left px-3 py-2">Lieferant</th>
                            <th className="text-left px-3 py-2">Empfänger</th>
                            <th className="text-left px-3 py-2">Beleg-Nr.</th>
                            <th className="text-left px-3 py-2">Typ</th>
                            <th className="text-left px-3 py-2">Quelle</th>
                            <th className="text-left px-3 py-2" title="Standort, auf den die Ware gebucht wurde">Standort</th>
                            <th className="text-right px-3 py-2">Pos.</th>
                            <th className="text-right px-3 py-2">Brutto €</th>
                            <th className="text-left px-3 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody data-testid="scan-history-rows">
                        {items.map((s) => (
                            <tr key={s.id} className="border-t border-gray-100" data-testid={`scan-history-row-${s.id}`}>
                                <td className="px-3 py-2 whitespace-nowrap text-xs">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-3 py-2">{s.supplier_name || "—"}</td>
                                <td className="px-3 py-2 text-xs">{s.recipient_name || "—"}</td>
                                <td className="px-3 py-2 font-mono-num text-xs">{s.delivery_note_number || "—"}</td>
                                <td className="px-3 py-2 text-xs">{DOC_LABEL[s.doc_type] || s.doc_type}</td>
                                <td className="px-3 py-2 text-xs" data-testid={`scan-history-origin-${s.id}`}><OriginBadge origin={s.origin} /></td>
                                <td className="px-3 py-2 text-xs" data-testid={`scan-history-site-${s.id}`}>
                                    {s.booked_site_code
                                        ? <span className="inline-flex text-[10px] font-bold bg-gray-100 text-gray-700 rounded px-1.5 py-0.5" title="Standort, auf den die Ware gebucht wurde">{s.booked_site_code}</span>
                                        : <span className="text-gray-300">—</span>}
                                </td>
                                <td className="px-3 py-2 text-right font-mono-num">{s.item_count}</td>
                                <td className="px-3 py-2 text-right font-mono-num">{Number(s.total_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1">
                                        {["own_invoice", "maintenance_coupon"].includes(s.doc_type) && (
                                            s.queued_evening ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-sky-100 text-sky-800 rounded px-1.5 py-0.5" title={`Wird abends gesammelt an ${s.sent_to_customer || "den Kunden"} gesendet`} data-testid={`scan-history-own-evening-${s.id}`}>
                                                    Abend-Versand
                                                </span>
                                            ) : s.sent_to_customer ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5" title={`Versendet an ${s.sent_to_customer}`} data-testid={`scan-history-own-sent-${s.id}`}>
                                                    an Kunden versendet
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5" title={s.send_skipped_reason || ""} data-testid={`scan-history-own-notsent-${s.id}`}>
                                                    nicht versendet
                                                </span>
                                            )
                                        )}
                                        {s.file_url && (
                                            <a
                                                href={normalizeImageUrl(s.file_url)}
                                                target="_blank" rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-[10px] font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 rounded px-1.5 py-0.5"
                                                data-testid={`scan-history-original-link-${s.id}`}
                                                title="Original-Beleg (PDF/Foto) ansehen"
                                            >
                                                <Eye className="w-3 h-3" /> Original
                                            </a>
                                        )}
                                        {s.is_commission && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">
                                                <ShoppingCart className="w-3 h-3" /> Kommission
                                            </span>
                                        )}
                                        {s.supplier_invoice_id && (
                                            s.is_commission ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-100 text-blue-800 rounded px-1.5 py-0.5" title="Rechnung in der Buchhaltung der Ziel-Firma (Kommission)">
                                                    <Landmark className="w-3 h-3" /> Buchhaltung Ziel-Firma
                                                </span>
                                            ) : (
                                                <Link
                                                    to={`/dealer/buchhaltung?tab=kreditoren&focus=${s.supplier_invoice_id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-100 hover:bg-blue-200 text-blue-800 rounded px-1.5 py-0.5"
                                                    data-testid={`scan-history-accounting-link-${s.id}`}
                                                    title="Zur passenden Eingangsrechnung in der Buchhaltung springen"
                                                >
                                                    <Landmark className="w-3 h-3" /> zur Buchhaltung <ArrowRight className="w-3 h-3" />
                                                </Link>
                                            )
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-400 text-sm">Noch keine Scans{q ? " für diese Suche" : ""}.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {loading && <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
        </div>
    );
}
